import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  Code as CodeIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  HourglassEmpty as HourglassIcon,
  PlayCircleOutline as InProgressIcon,
  CheckCircleOutline as ClearBlockedIcon,
  TaskOutlined as CompletedIcon,
  InboxOutlined as EmptyIcon,
} from "@mui/icons-material";
import type { EndpointRequest, RequestStatus } from "../types";
import {
  ModernMethodChip,
  ModernPriorityChip,
} from "./ModernUiComponents";

interface EndpointKanbanViewProps {
  requests: EndpointRequest[];
  onViewRequest: (request: EndpointRequest) => void;
  onOpenCodeSnippet: (request: EndpointRequest) => void;
  onStatusChange: (id: string, newStatus: RequestStatus) => void;
  primaryColor: string;
}

const COLUMNS: {
  status: RequestStatus;
  title: string;
  color: string;
  emptyLabel: string;
  emptySubtext: string;
  icon: React.ReactNode;
}[] = [
  {
    status: "pending",
    title: "Pending",
    color: "#F59E0B",
    emptyLabel: "No Pending Requests",
    emptySubtext: "New requests will show here",
    icon: <HourglassIcon sx={{ fontSize: 26, color: "#F59E0B", opacity: 0.8 }} />,
  },
  {
    status: "in_progress",
    title: "In Progress",
    color: "#3B82F6",
    emptyLabel: "No Active Tasks",
    emptySubtext: "Claim or start a request to track progress",
    icon: <InProgressIcon sx={{ fontSize: 26, color: "#3B82F6", opacity: 0.8 }} />,
  },
  {
    status: "blocked",
    title: "Blocked",
    color: "#EF4444",
    emptyLabel: "All Clear",
    emptySubtext: "No blocked endpoints requiring attention",
    icon: <ClearBlockedIcon sx={{ fontSize: 26, color: "#10B981", opacity: 0.8 }} />,
  },
  {
    status: "completed",
    title: "Completed",
    color: "#10B981",
    emptyLabel: "No Completed Requests",
    emptySubtext: "Finished endpoints will be archived here",
    icon: <CompletedIcon sx={{ fontSize: 26, color: "#10B981", opacity: 0.8 }} />,
  },
];

export const EndpointKanbanView: React.FC<EndpointKanbanViewProps> = ({
  requests,
  onViewRequest,
  onOpenCodeSnippet,
  onStatusChange,
  primaryColor,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; request: EndpointRequest } | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, req: EndpointRequest) => {
    event.stopPropagation();
    setMenuAnchor({ element: event.currentTarget, request: req });
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleSelectStatus = (newStatus: RequestStatus) => {
    if (menuAnchor?.request) {
      if (newStatus === "completed" && (!menuAnchor.request.endpoint || !menuAnchor.request.endpoint.trim())) {
        onViewRequest(menuAnchor.request);
        handleCloseMenu();
        return;
      }
      if (menuAnchor.request.id) {
        onStatusChange(menuAnchor.request.id, newStatus);
      }
    }
    handleCloseMenu();
  };

  const getSlaBadge = (targetDateStr?: string, status?: RequestStatus) => {
    if (!targetDateStr || status === "completed") return null;
    const target = new Date(targetDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return (
        <Chip
          label="Overdue"
          size="small"
          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800, bgcolor: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5" }}
        />
      );
    } else if (diffDays === 0) {
      return (
        <Chip
          label="Due Today"
          size="small"
          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800, bgcolor: "#FFF7ED", color: "#C2410C", border: "1px solid #FDBA74" }}
        />
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        },
        gap: 2.5,
        alignItems: "start",
      }}
    >
      {COLUMNS.map((col) => {
        const columnRequests = requests.filter((r) => r.status === col.status);

        return (
          <Paper
            key={col.status}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              backgroundColor: "rgba(248, 250, 252, 0.75)",
              border: "1px solid rgba(226, 232, 240, 0.9)",
              minHeight: "180px",
              display: "flex",
              flexDirection: "column",
              transition: "all 0.2s ease",
            }}
          >
            {/* Column Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                pb: 1.5,
                borderBottom: `2px solid ${col.color}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: col.color,
                  }}
                />
                <Typography variant="subtitle1" fontWeight="700" color="text.primary">
                  {col.title}
                </Typography>
              </Box>

              <Chip
                label={columnRequests.length}
                size="small"
                sx={{
                  fontWeight: 700,
                  height: 22,
                  fontSize: "0.75rem",
                  backgroundColor: `${col.color}15`,
                  color: col.color,
                }}
              />
            </Box>

            {/* Request Cards Container */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.75,
                flexGrow: 1,
              }}
            >
              {columnRequests.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    border: "1.5px dashed rgba(203, 213, 225, 0.8)",
                    borderRadius: "14px",
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "130px",
                  }}
                >
                  <Box sx={{ mb: 1 }}>{col.icon}</Box>
                  <Typography variant="subtitle2" fontWeight="700" color="text.primary" fontSize="0.825rem">
                    {col.emptyLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.725rem" sx={{ mt: 0.25 }}>
                    {col.emptySubtext}
                  </Typography>
                </Paper>
              ) : (
                columnRequests.map((req) => (
                  <Card
                    key={req.id}
                    elevation={0}
                    onClick={() => onViewRequest(req)}
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid rgba(226, 232, 240, 0.9)",
                      backgroundColor: "background.paper",
                      cursor: "pointer",
                      transition: "transform 0.18s ease, box-shadow 0.18s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 10px 24px -4px rgba(0,0,0,0.08)",
                        borderColor: `${primaryColor}60`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      {/* Top Chips row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                          <ModernMethodChip method={req.method} />
                          <ModernPriorityChip priority={req.priority} />
                          {getSlaBadge(req.targetDeliveryDate, req.status)}
                        </Box>

                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, req)}
                          sx={{ p: 0.5 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Component Name */}
                      <Typography variant="caption" color="primary.main" fontWeight="700" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", display: "block" }}>
                        {req.component}
                      </Typography>

                      {/* Task Description */}
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{
                          mt: 0.25,
                          mb: 1.5,
                          color: "text.primary",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                        }}
                      >
                        {req.task}
                      </Typography>

                      {/* Endpoint preview pill if exists */}
                      {req.endpoint && (
                        <Box
                          sx={{
                            p: 0.75,
                            mb: 1.5,
                            borderRadius: "8px",
                            backgroundColor: "action.hover",
                            fontFamily: "monospace",
                            fontSize: "0.725rem",
                            color: "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {req.endpoint}
                        </Box>
                      )}

                      {/* Footer Info */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          pt: 1,
                          borderTop: "1px solid rgba(241, 245, 249, 1)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                          <Tooltip title={`Requested by: ${req.lead}`}>
                            <Avatar sx={{ width: 22, height: 22, fontSize: "0.7rem", bgcolor: primaryColor }}>
                              {req.lead?.charAt(0).toUpperCase() || <PersonIcon sx={{ fontSize: 12 }} />}
                            </Avatar>
                          </Tooltip>
                          {req.assignedBackend ? (
                            <Tooltip title={`Assigned Dev: ${req.assignedBackend.email}`}>
                              <Chip
                                avatar={
                                  <Avatar sx={{ width: 18, height: 18, fontSize: "0.65rem", bgcolor: "#334155", color: "white" }}>
                                    {(req.assignedBackend.first_name || req.assignedBackend.username || "D").charAt(0).toUpperCase()}
                                  </Avatar>
                                }
                                label={req.assignedBackend.first_name ? req.assignedBackend.first_name : req.assignedBackend.username}
                                size="small"
                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, backgroundColor: "#F1F5F9", color: "#334155" }}
                              />
                            </Tooltip>
                          ) : req.assignedBackendEmail ? (
                            <Tooltip title={`Assigned Dev: ${req.assignedBackendEmail}`}>
                              <Chip
                                avatar={
                                  <Avatar sx={{ width: 18, height: 18, fontSize: "0.65rem", bgcolor: "#334155", color: "white" }}>
                                    {req.assignedBackendEmail.charAt(0).toUpperCase()}
                                  </Avatar>
                                }
                                label={req.assignedBackendEmail}
                                size="small"
                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, backgroundColor: "#F1F5F9", color: "#334155" }}
                              />
                            </Tooltip>
                          ) : null}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="View Code Snippet">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenCodeSnippet(req);
                              }}
                              sx={{ p: 0.5, color: "action.active" }}
                            >
                              <CodeIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {req.targetDeliveryDate && (
                            <Tooltip title={`Target Delivery: ${req.targetDeliveryDate}`}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "text.secondary" }}>
                                <ScheduleIcon sx={{ fontSize: 14 }} />
                                <Typography variant="caption" fontSize="0.7rem" fontWeight="600">
                                  {req.targetDeliveryDate.slice(5)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Paper>
        );
      })}

      {/* Context Menu for Status Actions */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        slotProps={{ paper: { sx: { borderRadius: "14px", minWidth: 180 } } }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.75, display: "block", color: "text.secondary", fontWeight: 700 }}>
          Move Status To:
        </Typography>

        {COLUMNS.map((col) => (
          <MenuItem
            key={col.status}
            disabled={menuAnchor?.request.status === col.status}
            onClick={() => handleSelectStatus(col.status)}
            sx={{ fontSize: "0.85rem", gap: 1.5 }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col.color }} />
            <ListItemText primary={col.title} primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
